import { createEffect, untrack } from "solid-js"
import { Color, DoubleSide, Group, InstancedMesh, Mesh, MeshBasicMaterial, MeshLambertMaterial, PlaneGeometry, PointLight, Vector3, type Scene } from "three"
import { onCleanupRemoveAndDispose } from "../../../rx/cleanup/onCleanupRemoveAndDispose"
import { createSharedTileGraphNodeCollectionMemo } from "../../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import useGridWidthHeight from "../../../rx/memo/useGridWidthHeight"
import { useSelectedPositionListSignal } from "../../../rx/signal/useSelectedPositionListSignal"
import { useSelectedPositionSignal } from "../../../rx/signal/useSelectedPositionSignal"
import type { Engine } from "../../core/engine/Engine.type"
import type { TileGraphNode } from "../../core/engine/TileGraphNode/TileGraphNode.type"
import { ELEVATION_SIZE } from "../../core/entity/EntityElevation/elevation_consts"
import { sin } from "../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import createOnFrameUpdateIsActiveSignal from "../../event/GameObject3D/GameObject3DFrameUpdate/createOnFrameUpdateIsActiveSignal"
import { VERTICES_PER_TILE } from "../consts"
import { Cursor_Arrow1_GLTF_Model, Cursor_TileCursorStyle1_GLTF_Model } from "../gltf_models.generated"
import { dummyMatrix, dummyObject3D, dummyPosition, resetDummyObject3D } from "../objects/DummyObject3D/dummyObject3D"
import createSquareCanvasTexture from "../texture/createSquareCanvasTexture"
import { appGltfLoader } from "./appGltfLoader"
import createDebugTextSprite from "./createDebugTextSprite"
import onGltfLoaderError from "./onGltfLoaderError"
import removeDebugTextObject from "./removeDebugTextObject"
import rotatePlaneToLayFlat from "./rotatePlaneToLayFlat"
import setObjPosition from "./setObjPosition"
import { RenderOrder } from "../RenderOrder.enum"

const selectedPositionColorHex = 0xfffd67

const SELECTED_POSITION_SIZE = VERTICES_PER_TILE * 0.9
const defaultPositionY = 0.5

// TODO rewrite to support multiple selected positions
export default function createSelectedPositionMesh(
  engine: Engine,
  scene: Scene
): void {
  const group: Group = new Group()
  group.name = 'SelectedPosition'
  group.scale.multiplyScalar(VERTICES_PER_TILE / 32)

  const getGridSize = useGridWidthHeight(engine)
  const [getSelectedPosition] = useSelectedPositionSignal(engine)
  const [getSelectedPositionList] = useSelectedPositionListSignal(engine)

  appGltfLoader.load(Cursor_TileCursorStyle1_GLTF_Model, function (ModelObj) {
    const { scene } = ModelObj
    group.add(scene)

    const cursor = group.getObjectByName('Cursor') as Mesh
    const material = cursor.material as MeshBasicMaterial
    material.transparent = true
    material.opacity = 0.25
    material.color = new Color(selectedPositionColorHex)

    mountOnFrameUpdateToObject3D(engine, group, function (elapsedMs: number, frameAt: number) {
      const s1 = sin(frameAt / 500)
      group.position.y = defaultPositionY + (1 + (s1 * s1) / 2)

      const s2 = sin(frameAt / 800)
      material.opacity = 0.5 + ((1 + s2 * s2) / 32)

      group.rotateY(elapsedMs / -600)
    })
  }, undefined, onGltfLoaderError)

  appGltfLoader.load(Cursor_Arrow1_GLTF_Model, function (ModelObj) {
    const { scene } = ModelObj

    const ArrowGroup = scene
    // const ArrowHeadMesh = scene.getObjectByName('Head') as Mesh
    const ArrowTailMesh = scene.getObjectByName('Tail') as Mesh
    const ArrowTailMesh2 = ArrowTailMesh.clone()
    ArrowTailMesh2.position.y += 2
    ArrowGroup.add(ArrowTailMesh2)

    ArrowGroup.scale.multiply(new Vector3(2, 2, 0.25))

    group.add(ArrowGroup)
    ArrowGroup.position.y += 20
  }, undefined, onGltfLoaderError)

  group.visible = false

  scene.add(group)

  const lightPointingDown = new PointLight(0xffffff, 20)
  lightPointingDown.castShadow = false
  lightPointingDown.position.y = defaultPositionY + 20
  lightPointingDown.lookAt(0, defaultPositionY + 15, 0)
  group.add(lightPointingDown)

  createEffect(() => {
    const selectedPosition = getSelectedPosition()

    let newValue: boolean = false

    if (selectedPosition) {
      const stateX = selectedPosition.x
      const stateY = selectedPosition.y

      // const debugText = createDebugTextSprite(`(${stateX}, ${stateY})`)
      const debugText = createDebugTextSprite(`Selected\n(${stateX}, ${stateY})`)
      // debugText.scale.set(2, 2, 2) // Scale the text
      debugText.position.set(0, 9, 0) // Position the text above the box
      debugText.scale.set(16, 16, 0.1) // Adjust size to match box
      removeDebugTextObject(group)
      group.add(debugText)

      setObjPosition(group, selectedPosition, defaultPositionY)

      newValue = true
    }
    group.visible = newValue
  })

  const texture = createSquareCanvasTexture()

  const getTileGraphNodeList = createSharedTileGraphNodeCollectionMemo(engine)
  createEffect(() => {
    const gridSize = getGridSize()
    const { width: gridWidth } = gridSize
    const tileGraphNodeList = getTileGraphNodeList()
    const selectedPositionList = getSelectedPositionList()
    if (selectedPositionList.length > 1) {
      // console.log('createSelectedPositionMesh.selectedPositionList.length > 1', selectedPositionList.length)
      const geometry = new PlaneGeometry(SELECTED_POSITION_SIZE, SELECTED_POSITION_SIZE)
      rotatePlaneToLayFlat(geometry)

      const material = new MeshLambertMaterial({
        color: 0xff0000,
        depthWrite: false,
        depthTest: true,
        side: DoubleSide,
        transparent: true,
        opacity: 0.5,
        map: texture,
      })
      const im = new InstancedMesh(geometry, material, selectedPositionList.length)
      im.renderOrder = RenderOrder.SelectedPositionTileCursor
      resetDummyObject3D()

      for (let i = 0; i < selectedPositionList.length; i++) {
        const { x: gridX, y: gridY } = selectedPositionList[i]
        const nodeIndex = gridY * gridWidth + gridX
        const tileGraphNode = tileGraphNodeList[nodeIndex]
        const wx = tileGraphNode.x * VERTICES_PER_TILE
        const wy = tileGraphNode.y * VERTICES_PER_TILE

        let currentElevation = (untrack(tileGraphNode.floor_e) + defaultPositionY) * ELEVATION_SIZE
        let targetElevation = currentElevation

        dummyPosition.set(wx, currentElevation, wy)
        dummyObject3D.updateMatrix()
        im.setMatrixAt(i, dummyMatrix)

        createEffect(function updateTargetElevation() {
          targetElevation = getFreshTargetElevation(tileGraphNode)
          if (targetElevation !== currentElevation) {
            setIsActive(true)
          }
        })

        const [, setIsActive] = createOnFrameUpdateIsActiveSignal(engine, (dt: number) => {
          // console.log('updateTargetElevation', {targetElevation, currentElevation})
          const elevationDiff = targetElevation - currentElevation
          currentElevation += elevationDiff * (dt / 500)
          // console.log('updateTargetElevation.frame.elevationDiff', elevationDiff)
          if (Math.abs(elevationDiff) < 0.01) {
            currentElevation = targetElevation
            setIsActive(false)
          }

          resetDummyObject3D()
          dummyPosition.set(wx, currentElevation, wy)
          dummyObject3D.updateMatrix()
          im.setMatrixAt(i, dummyMatrix)
          im.instanceMatrix.needsUpdate = true
        })
      }

      im.frustumCulled = false
      im.castShadow = false
      im.receiveShadow = false

      scene.add(im)
      onCleanupRemoveAndDispose(im)
    }
  })


  onCleanupRemoveAndDispose(group)
}

function getFreshTargetElevation(tileGraphNode: TileGraphNode): number {
  const tileSurfaceElevation = Math.max(tileGraphNode.floor_e(), tileGraphNode.water_e())
  return (tileSurfaceElevation + defaultPositionY) * ELEVATION_SIZE
}